import React, { useEffect, useState } from "react";
import { Badge, Col, Dropdown, FormControl, InputGroup, Placeholder, Row } from "react-bootstrap";

import translate from "../../services/Translate";
import DstButton from "../DstButton/DstButton";
import CheckIconRounded from "../DstIcons/CheckIconRounded";
import Plus from "../DstIcons/Plus";
import { MultiSelectDropdownOptions } from "./MultiSelectDropdown.d";
import "./MultiSelectDropdown.scss";

const MultiSelectDropdown = ({
  badgeItemProperty,
  btnWidth = "120px",
  borderToggle = false,
  clearOnConfirm,
  clearItemsOnSend = false,
  confirmOnClose = false,
  defaultSelectedItems = [],
  disabled = false,
  displayItem,
  hideCaret = false,
  hideOnConfirm = true,
  isSingleSelect = false,
  isSingleSelectRemovable = false,
  items,
  label,
  language,
  mock = false,
  onConfirm,
  selectedItemLabel,
  showAlwaysConfirm,
  toggleClass = "min-w-100px",
}: MultiSelectDropdownOptions) => {
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState<number | null>(null);
  const [hasUserCleared, setHasUserCleared] = useState(false);

  useEffect(() => {
    if (defaultSelectedItems && defaultSelectedItems.length > 0 && !hasUserCleared) {
      setSelectedItems(defaultSelectedItems);
    }
  }, [defaultSelectedItems, hasUserCleared]);

  const _isItemDisabled = (itemId: number) => {
    return !isSingleSelect && selectedItems.some((selectedItem) => selectedItem.incompatible?.includes(itemId));
  };

  const _handleSelectItem = (item: any, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (isSingleSelect) {
      setSelectedItems([item]);
      onConfirm([item]);
      setDropdownOpen(false);
    } else if (!selectedItems.some((selectedItem) => selectedItem.id === item.id)) {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const _handleRemoveItem = (itemToRemove: any) => {
    const newSelectedItems = selectedItems.filter((item) => item.id !== itemToRemove.id);
    setSelectedItems(newSelectedItems);
    if (newSelectedItems.length === 0) {
      setHasUserCleared(true);
    }
  };

  const _handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const _isItemSelected = (itemId: number) => selectedItems.some((item) => item.id === itemId);

  const _filteredItems = items.filter(
    (item) =>
      !searchTerm ||
      Object.values(item).some(
        (property: any) =>
          ["string", "number"].includes(typeof property) &&
          String(property).toLowerCase().includes(searchTerm.toLowerCase())
      )
  );

  const handleConfirmClick = () => {
    onConfirm(selectedItems);
    if (clearOnConfirm) {
      setSelectedItems([]);
      setHasUserCleared(true);
    }
    setDropdownOpen(!hideOnConfirm);
  };

  useEffect(() => {
    if (clearItemsOnSend) {
      setSelectedItems([]);
    }
  }, [clearItemsOnSend]);

  if (mock) {
    return (
      <Placeholder animation="glow">
        <Placeholder.Button
          className="h-100"
          style={{
            width: btnWidth,
          }}
          aria-hidden="true"
        />
      </Placeholder>
    );
  }

  return (
    <Dropdown
      className="multi-select-dropdown"
      show={dropdownOpen}
      onToggle={(isOpen) => {
        if (disabled) {
          return;
        }
        setDropdownOpen(isOpen);
        if (!isOpen && confirmOnClose && !isSingleSelect) {
          onConfirm(selectedItems);
        }
      }}
    >
      <Dropdown.Toggle
        variant="input"
        className={`text-left align-items-center justify-content-between d-flex ${
          borderToggle ? "border" : ""
        } ${toggleClass} ${hideCaret ? "caret-hide" : ""}`}
      >
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu className="p-3 min-w-450px s-regular">
        <InputGroup className="mb-3">
          <FormControl
            placeholder={translate(language, "COMPONENTS.MULTI_SELECT.SEARCH_PLACEHOLDER")}
            value={searchTerm}
            onChange={_handleSearchChange}
            className="s-regular"
          />
        </InputGroup>
        <div style={{ maxHeight: "200px", overflowY: "auto" }}>
          {_filteredItems.map((item) => (
            <Dropdown.Item
              key={item.id}
              onClick={(e) => _handleSelectItem(item, e)}
              className="px-0"
              onMouseEnter={() => setHoveredItemId(item.id)}
              onMouseLeave={() => setHoveredItemId(null)}
              onFocus={() => setHoveredItemId(item.id)}
              onBlur={() => setHoveredItemId(null)}
              disabled={_isItemDisabled(item.id)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex w-100 position-relative">
                  <div className="me-80px">{displayItem(item)}</div>
                  {_isItemSelected(item.id) && (
                    <div className="d-flex align-items-center position-absolute end-0 ">
                      <CheckIconRounded />
                      <span>{selectedItemLabel ?? translate(language, "COMPONENTS.MULTI_SELECT.ADDED")}</span>
                    </div>
                  )}

                  {!_isItemSelected(item.id) && hoveredItemId === item.id && (
                    <div className="position-absolute end-0">
                      <Plus color="var(--color-light-lavender)" bgColor="var(--color-blue)" />
                    </div>
                  )}
                </div>
              </div>
            </Dropdown.Item>
          ))}
        </div>
        {!isSingleSelect && (
          <Row>
            <Col md={9}>
              <div className="selected-items mt-2">
                {selectedItems.map((item, index) => (
                  <Badge
                    key={`chosen-${index}`}
                    pill
                    className="selected-item-badge me-1 d-inline-flex w-fit align-items-center"
                    bg="dropdown"
                  >
                    <span>{item[badgeItemProperty]}</span>
                    <span className="dst-icon-x clickable" onClick={() => _handleRemoveItem(item)} />
                  </Badge>
                ))}
                <div className="xs-regular">
                  {selectedItems.length}{" "}
                  {selectedItems.length > 1
                    ? translate(language, "COMPONENTS.MULTI_SELECT.ITEMS")
                    : translate(language, "COMPONENTS.MULTI_SELECT.ITEM")}{" "}
                  {translate(language, "COMPONENTS.MULTI_SELECT.SELECTED")}
                </div>
              </div>
            </Col>
            <Col md={3} className="text-end align-self-end">
              {(showAlwaysConfirm || selectedItems.length > 0) && !confirmOnClose && (
                <DstButton
                  btnClass="mt-2"
                  clickFunction={handleConfirmClick}
                  value={translate(language, "GLOBAL.CONFIRM")}
                  btnSize="lg"
                />
              )}
            </Col>
          </Row>
        )}
        {isSingleSelect && isSingleSelectRemovable && selectedItems.length > 0 && (
          <div className="mt-2">
            <Badge pill className="selected-item-badge me-1 d-inline-flex w-fit align-items-center" bg="dropdown">
              <span>{selectedItems[0][badgeItemProperty]}</span>
              <span
                className="dst-icon-x clickable"
                onClick={() => {
                  setSelectedItems([]);
                  onConfirm([]);
                }}
              ></span>
            </Badge>
            <div className="xs-regular mt-1">
              1 {translate(language, "COMPONENTS.MULTI_SELECT.ITEM")}{" "}
              {translate(language, "COMPONENTS.MULTI_SELECT.SELECTED")}
            </div>
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default MultiSelectDropdown;
